import { Button } from '@crowdex/ui/components/button';
import { Input } from '@crowdex/ui/components/input';
import { cn } from '@crowdex/ui/lib/utils';
import { ArrowUp } from 'lucide-react';
import { type ReactNode, type RefObject, useEffect } from 'react';
import { FLOATING_COMPOSER_SHORTCUT } from '@/components/desktop-shell/floating-composer-chips';
import { matchesShortcut, shortcutLabel } from '@/lib/platform';

/**
 * The floating composer of a page outside a conversation (D58): an item's page ("Ask about
 * this…") and an app's home (a new item). One line, its send button, ⌘L reaching it; `leading`
 * is what comes before the text (who it writes to), `className` where it floats.
 */
export function FloatingComposerForm({
  label,
  inputLabel,
  placeholder,
  value,
  onChange,
  onSend,
  sending,
  input,
  leading,
  className,
  ...data
}: {
  label: string;
  inputLabel: string;
  placeholder: string;
  value: string;
  onChange(value: string): void;
  onSend(text: string): void;
  sending: boolean;
  input: RefObject<HTMLInputElement | null>;
  leading?: ReactNode;
  className?: string;
} & { [key: `data-${string}`]: string | boolean | undefined }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!matchesShortcut(event, FLOATING_COMPOSER_SHORTCUT)) return;
      event.preventDefault();
      input.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [input]);
  const empty = value.trim() === '';
  return (
    <form
      {...data}
      aria-label={label}
      className={cn(
        'flex items-center gap-1.5 rounded-lg border border-hairline-strong bg-card p-1.5 shadow-lg',
        leading === undefined ? 'pl-3' : 'pl-2',
        className,
      )}
      onSubmit={(event) => {
        event.preventDefault();
        const text = value.trim();
        if (text === '' || sending) return;
        onSend(text);
      }}
    >
      {leading}
      <Input
        aria-label={inputLabel}
        appearance="bare"
        className="h-8 min-w-0 flex-1 px-1"
        onChange={(event) => onChange(event.target.value)}
        placeholder={`${placeholder} ${shortcutLabel(FLOATING_COMPOSER_SHORTCUT)}`}
        ref={input}
        value={value}
      />
      <Button
        aria-label="Send"
        className="size-8 shrink-0"
        disabled={empty || sending}
        size="icon-sm"
        type="submit"
        variant={empty ? 'secondary' : 'default'}
      >
        <ArrowUp className="size-4" />
      </Button>
    </form>
  );
}
